import type { ActiveSession, ProvisioningState, RunStatus } from '@lyra/shared';
import { ApiClientError } from './apiClient';

/**
 * Pure helpers for the Fleet provisioning UI states (Task 8) — factored out
 * of the Panou instance card (`features/panou/Instanta.tsx`) and the Mapări
 * run view (`features/mapari/MaparePage.tsx`) so the status→UI-state
 * mapping and the "show provisioning banner" predicate are unit-testable
 * without a DOM harness (the repo has none — see task brief).
 */

/** The Panou instance card's three renderable states, derived from
 *  `useActiveSession()`'s data. `null`/absent → 'idle' (no active session —
 *  render the "Pornește instanța" CTA). A `provisioningState` of
 *  `'provisioning'` → 'provisioning' (Fleet still bringing the instance up).
 *  Anything else — `null` (Fleet off, today's behaviour), `'ready'`, or any
 *  other post-ready value the session might carry — → 'ready': the instance
 *  is usable, show the live rate/cost. */
export type InstanceCardState = 'idle' | 'provisioning' | 'ready';

export function instanceCardState(active: ActiveSession | null | undefined): InstanceCardState {
  if (!active) return 'idle';
  return active.provisioningState === 'provisioning' ? 'provisioning' : 'ready';
}

/**
 * `POST /instance-sessions/start` error → an inline, display-ready Romanian
 * message, or `null` when nothing should be shown. Every non-2xx response
 * (409 conflict, 403 refused, 402 no_credit, 503 no_capacity, 500
 * config_error/provider_error) carries `{ error: string }` and `apiFetch`
 * already turns that into `ApiClientError.message` — so for every status
 * except 409 this is just a pass-through of the server's own text.
 *
 * 409 (a colleague started/stopped a session concurrently, closing a race)
 * is deliberately not surfaced: `useStartSession`'s `onSettled` invalidation
 * already refetches the active session so the UI self-corrects, and showing
 * a message here would just be stale noise the next render throws away.
 */
export function startSessionErrorMessage(err: unknown): string | null {
  if (err instanceof ApiClientError) {
    if (err.status === 409) return null;
    return err.message;
  }
  return 'Pornirea a eșuat. Reîncercați.';
}

/**
 * `MaparePage`'s run view (brief step 2): while a run is still `queued`
 * (hasn't reached `processing` yet) AND the org's active session is still
 * `provisioning`, show the "Se pornește instanța…" banner in place of
 * `LiveProgress`'s progress bar — Task 6's queue-and-auto-start gate holds
 * the run in `queued` until the instance reports `ready`, so this is exactly
 * the window where a bare progress bar would be misleading (0/N with no
 * movement, no instance running yet).
 */
export function shouldShowProvisioningBanner(
  runStatus: RunStatus,
  provisioningState: ProvisioningState | null | undefined
): boolean {
  return runStatus === 'queued' && provisioningState === 'provisioning';
}
